/**
 * Auth store — exposes the current Supabase user (if any) and a loading flag.
 *
 * Populated by AuthProvider, which subscribes to supabase.auth.onAuthStateChange.
 * Components read from this store via useAuthStore().
 *
 * The store is intentionally tiny: just `user`, `isLoading`, and setters.
 * Auth flow logic lives in lib/auth.ts; UI lives in pages/auth/*.
 */

import { create } from 'zustand';
import type { User } from '@supabase/supabase-js';

interface AuthState {
  /** The current authenticated user, or null if signed out / not configured */
  user: User | null;
  /** True until the initial auth check completes on app load */
  isLoading: boolean;
  /** Set by AuthProvider when auth state changes */
  setUser: (user: User | null) => void;
  /** Set by AuthProvider when initial auth check completes */
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isLoading: true,
  setUser: (user) => set({ user }),
  setLoading: (loading) => set({ isLoading: loading }),
}));

/** Convenience selector hook: returns just the current user. */
export const useCurrentUser = () => useAuthStore((s) => s.user);

/** Convenience: true iff a user is authenticated. */
export const useIsAuthenticated = () => useAuthStore((s) => s.user !== null);
